<template>
    <div
        v-if="isCromi() && product"
        class="absolute -right-5 -top-5 z-20 rounded-full bg-black/20 backdrop-blur-sm">
        <div class="relative">
            <span
                class="pointer-events-none absolute inset-0 rounded-full border border-ash-200"></span>

            <div class="flex items-stretch">
                <Transition
                    @before-enter="onBeforeEnter"
                    @enter="onEnter"
                    @after-enter="onAfterEnter"
                    @before-leave="onBeforeLeave"
                    @leave="onLeave"
                    @after-leave="onAfterLeave">
                    <div
                        v-if="isAddedToCart"
                        class="overflow-hidden">
                        <BaseCalculateInput
                            v-model="count"
                            :min="0"
                            :max="product.max_cart_quantity ?? undefined"
                            class="h-full min-w-36 shrink-0 max-md:max-w-[8rem] md:min-w-40" />
                    </div>
                </Transition>

                <div class="relative">
                    <button
                        aria-label="В корзину"
                        :tabindex="isAddedToCart ? -1 : 0"
                        class="rounded-full border border-ash-200 bg-black/20 p-3 outline-none transition-[opacity,background-color] duration-300 hover:bg-ash-200/40 focus:bg-ash-200/40"
                        :class="
                            isAddedToCart
                                ? 'pointer-events-none absolute inset-0 opacity-0'
                                : 'relative opacity-100'
                        "
                        @click="toCart">
                        <AtomIcon name="cart-plus" />
                    </button>

                    <NuxtLink
                        to="/cart/"
                        aria-label="Перейти в корзину"
                        :tabindex="isAddedToCart ? 0 : -1"
                        class="block rounded-full border border-ash-200 bg-black/20 p-3 outline-none transition-[opacity,background-color] duration-300 hover:bg-ash-200/40 focus:bg-ash-200/40"
                        :class="
                            isAddedToCart
                                ? 'relative opacity-100'
                                : 'pointer-events-none absolute inset-0 opacity-0'
                        "
                        @click="closeToasts">
                        <AtomIcon name="cart-to-go" />
                    </NuxtLink>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { CartKey } from '~/stores/cart.store'

import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'
import BaseCalculateInput from '~/components/base/base-calculate-input/BaseCalculateInput.vue'

import { useCart } from '~/composables/useCart'
import { useCartCountModel } from '~/composables/useCartCountModel'

interface CartButtonProduct {
    slug?: CartKey | null
    max_cart_quantity?: number | string | null
}

const props = defineProps({
    product: {
        type: [Object, null] as PropType<CartButtonProduct | null>,
        default: null,
    },
})

const { addToCart, isInCart } = useCart()
const { closeAll: closeToasts } = useToastStore()

const isAddedToCart = computed(() => {
    return isInCart(props.product?.slug)
})

const count = useCartCountModel(() => props.product, { min: 0 })

function toCart() {
    addToCart({
        slug: props.product?.slug,
        count: 1,
    })
}

function onBeforeEnter(element: Element) {
    const target = element as HTMLElement

    target.style.width = '0'
    target.style.opacity = '0'
}

function onEnter(element: Element) {
    const target = element as HTMLElement
    const width = `${target.scrollWidth}px`

    target.style.transition = 'width 300ms ease, opacity 300ms ease'

    requestAnimationFrame(() => {
        target.style.width = width
        target.style.opacity = '1'
    })
}

function onAfterEnter(element: Element) {
    const target = element as HTMLElement

    target.style.width = 'auto'
    target.style.opacity = '1'
    target.style.transition = ''
}

function onBeforeLeave(element: Element) {
    const target = element as HTMLElement

    target.style.width = `${target.scrollWidth}px`
    target.style.opacity = '1'
    target.style.transition = 'width 300ms ease, opacity 300ms ease'
}

function onLeave(element: Element) {
    const target = element as HTMLElement

    requestAnimationFrame(() => {
        target.style.width = '0'
        target.style.opacity = '0'
    })
}

function onAfterLeave(element: Element) {
    const target = element as HTMLElement

    target.style.width = ''
    target.style.opacity = ''
    target.style.transition = ''
}
</script>
